import type { AppSettings } from '../types';
import { beginStep, finishStep } from '../lib/trace';
import { retryModelChunk } from './modelRetry';
import { graphiteUserHeader } from './requestHeaders';

const MAX_EMBEDDING_CHARS = 8_000;

export function splitEmbeddingText(text: string, limit = MAX_EMBEDDING_CHARS): string[] {
  const compact = text.trim();
  if (!compact) return [];
  const chunks: string[] = [];
  let current = '';
  for (const paragraph of compact.split(/\n{2,}/)) {
    const parts = paragraph.length <= limit
      ? [paragraph]
      : Array.from({ length: Math.ceil(paragraph.length / limit) }, (_, index) => paragraph.slice(index * limit, (index + 1) * limit));
    for (const part of parts) {
      if (current && current.length + part.length + 2 > limit) { chunks.push(current); current = ''; }
      current = current ? `${current}\n\n${part}` : part;
    }
  }
  if (current) chunks.push(current);
  return chunks;
}

function normalizedMean(vectors: Array<{ vector: number[]; weight: number }>) {
  const mean = new Array<number>(1024).fill(0);
  const total = vectors.reduce((sum, item) => sum + item.weight, 0) || 1;
  for (const item of vectors) for (let index = 0; index < mean.length; index++) mean[index] += item.vector[index] * item.weight / total;
  const norm = Math.sqrt(mean.reduce((sum, value) => sum + value * value, 0));
  return norm ? mean.map((value) => value / norm) : mean;
}

async function errorMessage(response: Response) {
  try {
    const body = await response.json() as { detail?: string; error?: { message?: string } };
    return body.detail || body.error?.message || `HTTP ${response.status}`;
  } catch { return `HTTP ${response.status}`; }
}

export async function embeddingVector(settings: AppSettings, text: string, traceId?: string, label = 'Embedding', onRetry?: (attempt: number, delayMs: number, chunkLabel: string) => void, maxChars = MAX_EMBEDDING_CHARS): Promise<number[]> {
  const chunks = splitEmbeddingText(text, maxChars);
  if (!chunks.length) throw new Error('Нельзя построить embedding пустого текста');
  const settled = await Promise.allSettled(chunks.map(async (chunk, index) => {
    const chunkLabel = `${label} · чанк ${index + 1}/${chunks.length}`;
    const request = { model: 'text-embedding-3-large', input: chunk, dimensions: 1024 };
    const stepId = traceId ? beginStep(traceId, chunkLabel, { endpoint: `${settings.embeddingsEndpoint.replace(/\/$/, '')}/v1/embeddings`, request }) : '';
    try {
      const result = await retryModelChunk(async () => {
        const response = await fetch(`${settings.embeddingsEndpoint.replace(/\/$/, '')}/v1/embeddings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${settings.serviceToken}`, 'X-Graphite-User': graphiteUserHeader(settings.userKey), ...(traceId ? { 'X-Graphite-Trace': traceId } : {}) },
          body: JSON.stringify(request),
        });
        if (!response.ok) throw new Error(`Embeddings недоступны: ${await errorMessage(response)}`);
        const body = await response.json() as { data?: Array<{ embedding?: number[] }> };
        const vector = body.data?.[0]?.embedding;
        if (!vector || vector.length !== 1024) throw new Error('Embedding имеет неверную размерность');
        return { vector, requestId: response.headers.get('X-Graphite-Request-ID') };
      }, (attempt, delayMs) => onRetry?.(attempt, delayMs, chunkLabel));
      if (traceId) finishStep(traceId, stepId, { dimensions: result.vector.length, requestId: result.requestId });
      return { vector: result.vector, weight: chunk.length };
    } catch (error) { if (traceId) finishStep(traceId, stepId, undefined, error); throw error; }
  }));
  const failed = settled.find((result): result is PromiseRejectedResult => result.status === 'rejected');
  if (failed) throw failed.reason;
  const vectors = settled.map((result) => (result as PromiseFulfilledResult<{ vector: number[]; weight: number }>).value);
  return vectors.length === 1 ? vectors[0].vector : normalizedMean(vectors);
}
